var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Select, message } from 'antd';
import services from 'services';
import EmptyComponent from 'components/EmptyComponent';
import DataTable from 'library/components/DataTable';
import { Chip, Mono, NameText, Muted, showColumn, statusColumn } from 'library/components/DataTable/cells';
import ListScreen, { ListAddButton, ListExportButton, listPager, usePagedRows } from 'library/components/ListScreen';
import localeTranslator from 'library/Hocs/LocalTranslator';
import { Employee } from 'constants/resources';
var ROLE_TONES = ['is-sky', 'is-indigo', 'is-amber', 'is-violet'];
var csvEscape = function (value) {
    var text = value.replace(/"/g, '""');
    return "\"".concat(text, "\"");
};
var downloadCsv = function (filename, rows) {
    var content = rows.map(function (row) { return row.map(csvEscape).join(','); }).join('\n');
    var blob = new Blob(["\uFEFF".concat(content)], { type: 'text/csv;charset=utf-8;' });
    var url = URL.createObjectURL(blob);
    var link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
};
var initialsFromName = function (name) {
    var parts = name.trim().split(/\s+/).filter(Boolean);
    if (!parts.length)
        return '??';
    if (parts.length === 1)
        return parts[0].slice(0, 2).toUpperCase();
    return "".concat(parts[0][0]).concat(parts[parts.length - 1][0]).toUpperCase();
};
var Employees = function (props) {
    var t = props.translate;
    var navigate = useNavigate();
    var employeeService = services.employeeService, jobTitleService = services.jobTitleService, permissionService = services.permissionService;
    var _a = employeeService.useEmployees(), employees = _a.data, isLoading = _a.isLoading;
    var jobTitles = jobTitleService.useJobTitles().data;
    var _b = useState(''), searchValue = _b[0], setSearchValue = _b[1];
    var _c = useState('all'), roleFilter = _c[0], setRoleFilter = _c[1];
    var _d = useState(false), exporting = _d[0], setExporting = _d[1];
    var debounceRef = useRef(undefined);
    var canAdd = permissionService.hasPermissionToAdd(Employee);
    var canShow = permissionService.hasPermissionToShow(Employee);
    var roleName = useCallback(function (employee) {
        var _a;
        return ((_a = jobTitleService
            .getJobTitleDataByID(jobTitles, employee.getJobTitleId())) === null || _a === void 0 ? void 0 : _a.getTitle()) || '';
    }, [jobTitleService, jobTitles]);
    var staff = useMemo(function () { return (employees || []).filter(function (employee) { return Boolean(employee.user); }); }, [employees]);
    var filtered = useMemo(function () {
        var query = searchValue.trim().toLowerCase();
        return staff.filter(function (employee) {
            if (roleFilter !== 'all' && employee.getJobTitleId() !== roleFilter) {
                return false;
            }
            if (!query)
                return true;
            var user = employee.getUser();
            var haystack = [
                user.getName(),
                roleName(employee),
                user.getEmail(),
                user.getMobile()
            ]
                .join(' ')
                .toLowerCase();
            return haystack.includes(query);
        });
    }, [staff, searchValue, roleFilter, roleName]);
    var _e = usePagedRows(filtered), page = _e.page, setPage = _e.setPage, pages = _e.pages, from = _e.from, to = _e.to, pageRows = _e.pageRows;
    var roleOptions = useMemo(function () { return __spreadArray([
        { value: 'all', label: t('allRoles') }
    ], (jobTitles || []).map(function (jobTitle) { return ({
        value: jobTitle.getId(),
        label: jobTitle.getTitle()
    }); }), true); }, [jobTitles, t]);
    var onSearchValueChange = function (event) {
        var value = event.target.value;
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(function () {
            setPage(1);
            setSearchValue(value);
        }, 300);
    };
    var goToNewEmployeeForm = function () { return navigate('/employees/new'); };
    var goToEmployeeProfile = useCallback(function (id) {
        if (!canShow)
            return;
        navigate("/employees/".concat(id));
    }, [canShow, navigate]);
    var exportEmployees = function () {
        if (exporting)
            return;
        if (!filtered.length) {
            message.warning(t('exportEmpty'));
            return;
        }
        setExporting(true);
        try {
            var rows = __spreadArray([
                [
                    t('columns.name'),
                    t('columns.role'),
                    t('columns.phone'),
                    t('columns.email'),
                    t('columns.salary'),
                    t('columns.status')
                ]
            ], filtered.map(function (employee) {
                var user = employee.getUser();
                return [
                    user.getName() || '',
                    roleName(employee),
                    user.getMobile() || '',
                    user.getEmail() || '',
                    String(employee.getSalary() || ''),
                    employee.deleted_at ? t('statusArchived') : t('statusActive')
                ];
            }), true);
            var stamp = new Date().toISOString().slice(0, 10);
            downloadCsv("employees-".concat(stamp, ".csv"), rows);
        }
        catch (_a) {
            message.error(t('exportFailed'));
        }
        finally {
            setExporting(false);
        }
    };
    var columns = useMemo(function () { return __spreadArray([
        {
            key: 'avatar',
            width: '56px',
            render: function (employee) {
                var tone = ROLE_TONES[Math.abs(employee.getJobTitleId() || 0) % ROLE_TONES.length];
                return _jsx("span", { className: "cidx-avatar ".concat(tone), children: initialsFromName(employee.getUser().getName() || '—') });
            }
        },
        { key: 'name', title: t('columns.name'), width: 'minmax(160px, 1.3fr)', render: function (employee) { return _jsx(NameText, { children: employee.getUser().getName() }); } },
        {
            key: 'role',
            title: t('columns.role'),
            width: 'minmax(120px, 1fr)',
            render: function (employee) {
                var role = roleName(employee);
                var tone = ROLE_TONES[Math.abs(employee.getJobTitleId() || 0) % ROLE_TONES.length];
                return role ? _jsx(Chip, { tone: tone, children: role }) : _jsx(Muted, {});
            }
        },
        { key: 'phone', title: t('columns.phone'), width: 'minmax(140px, 1fr)', render: function (employee) { return _jsx(Mono, { children: employee.getUser().getMobile() || '—' }); } },
        { key: 'email', title: t('columns.email'), width: 'minmax(180px, 1.4fr)', render: function (employee) { return _jsx(Muted, { children: employee.getUser().getEmail() }); } },
        { key: 'salary', title: t('columns.salary'), width: '120px', align: 'end', render: function (employee) { return _jsx(Mono, { variant: "strong", children: t('common:price', { amount: employee.getSalary() }) }); } },
        statusColumn({
            title: t('columns.status'),
            width: '110px',
            align: 'center',
            active: function (employee) { return !employee.deleted_at; },
            activeLabel: t('statusActive'),
            inactiveLabel: t('statusArchived')
        })
    ], (canShow ? [showColumn({
            title: t('columns.action'),
            label: t('common:buttons.show'),
            onShow: function (employee) { return goToEmployeeProfile(employee.getId()); }
        })] : []), true); }, [t, roleName, canShow, goToEmployeeProfile]);
    return (_jsx(ListScreen, { title: t('title'), count: t('countBadge', { count: staff.length }), subtitle: t('subtitle'), actions: (_jsxs(_Fragment, { children: [_jsx(ListExportButton, { label: t('export'), busyLabel: t('exporting'), busy: exporting, onClick: exportEmployees }), canAdd && _jsx(ListAddButton, { label: t('newEmployee'), onClick: goToNewEmployeeForm })] })), search: { placeholder: t('searchPlaceholder'), onChange: onSearchValueChange }, filters: (_jsx(Select, { className: "cidx-select", value: roleFilter, onChange: function (value) {
                setRoleFilter(value);
                setPage(1);
            }, options: roleOptions, popupMatchSelectWidth: false })), children: _jsx(DataTable, { minWidth: canShow ? '1040px' : '940px', loading: isLoading, rows: pageRows, rowKey: function (employee) { return employee.getId(); }, rowTestId: function (employee) { return "employee-row-".concat(employee.getId()); }, onRowClick: canShow ? function (employee) { return goToEmployeeProfile(employee.getId()); } : undefined, empty: _jsx(EmptyComponent, { description: t(staff.length ? 'empty' : 'emptyText') }), columns: columns, pager: listPager({
                total: filtered.length,
                page: page,
                pages: pages,
                meta: t('showingRange', { from: from, to: to, total: filtered.length }),
                previous: t('previous'),
                next: t('next'),
                center: t('pageOf', { page: page, pages: pages }),
                setPage: setPage
            }) }) }));
};
export default localeTranslator(Employees, 'employees');
